import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import DictationInput from '../../components/DictationInput';
import { useNavigate } from 'react-router-dom';
import { Trophy, CalendarDays, Users, FileText, Send, Building2, Home, Flame, Heart,
  ChevronRight, ChevronDown, ChevronUp, ArrowLeft, Upload, Paperclip, Eye,
  TrendingUp, Clock, CheckCircle, X, User } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HerReferralPortalPage() {
  const { user, token, axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [modules, setModules] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [drilldownClient, setDrilldownClient] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [form, setForm] = useState({
    name: '', dob: '', ethnicity: '', age_range: '', referral_source: '', race: '',
    reason_for_referral: '', additional_info: '', other_services_needed: '',
    referring_to: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Drill-down state
  const [expandedModule, setExpandedModule] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const [expandedFacilitator, setExpandedFacilitator] = useState(null);

  useEffect(() => {
    api.get('/her/participants').then(r => setParticipants(r.data)).catch(() => {});
    api.get('/her/completions').then(r => setCompletions(r.data)).catch(() => {});
    api.get('/her/sessions').then(r => setSessions(r.data)).catch(() => {});
    api.get('/her/facilitators').then(r => setFacilitators(r.data)).catch(() => {});
    api.get('/her/modules').then(r => setModules(r.data)).catch(() => {});
    api.get('/her/enrollments').then(r => setEnrollments(r.data)).catch(() => {});
  }, []);

  const completionsByParticipant = useMemo(() => {
    const m = {};
    completions.forEach(c => { if (!m[c.participant_id]) m[c.participant_id] = []; m[c.participant_id].push(c); });
    return m;
  }, [completions]);
  const enrollmentsByParticipant = useMemo(() => {
    const m = {};
    enrollments.forEach(e => { const key = e.client_id || e.participant_id; if (key) { if (!m[key]) m[key] = []; m[key].push(e); } });
    return m;
  }, [enrollments]);
  const activeEnrollments = enrollments.filter(e => e.status === 'enrolled' || e.status === 'active');
  const completedTotal = completions.filter(c => c.status === 'approved' || c.status === 'completed').length;
  const participantMap = useMemo(() => {
    const m = {};
    participants.forEach(p => { m[p.id] = p; });
    enrollments.forEach(e => {
      const cid = e.client_id || e.participant_id;
      if (cid && !m[cid] && e.client_name) {
        m[cid] = { id: cid, full_name: e.client_name, drug_of_choice: e.drug_of_choice, charges: e.charges, phase: e.phase };
      }
    });
    return m;
  }, [participants, enrollments]);

  const toggleService = (val) => {
    setForm(prev => ({ ...prev, referring_to: prev.referring_to.includes(val) ? prev.referring_to.filter(v => v !== val) : [...prev.referring_to, val] }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingFiles(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', file.name);
        fd.append('category', 'referral_document');
        const res = await fetch(`${API}/api/documents/upload`, { method: 'POST', credentials: 'include', body: fd });
        if (res.ok) { uploaded.push(await res.json()); }
      }
      setUploadedDocs(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} document(s) uploaded`);
    } catch { toast.error('Upload failed'); }
    finally { setUploadingFiles(false); }
  };

  const handleSubmitReferral = async () => {
    if (!form.name) return toast.error('Name is required');
    if (!form.reason_for_referral) return toast.error('Reason for referral is required');
    if (form.referring_to.length === 0) return toast.error('Select at least one service');
    setSubmitting(true);
    try {
      await api.post('/heat/submit-referral', {
        full_name: form.name, dob: form.dob, ethnicity: form.ethnicity, age_range: form.age_range,
        race: form.race, referral_source: form.referral_source, reason_for_referral: form.reason_for_referral,
        additional_info: form.additional_info, referring_to: form.referring_to.join(', '),
        other_services_needed: form.other_services_needed,
        uploaded_documents: uploadedDocs.map(d => ({ id: d.id, filename: d.filename })),
      });
      setSubmitted(true);
      toast.success('Referral submitted!');
      setForm({ name: '', dob: '', ethnicity: '', age_range: '', referral_source: '', race: '', reason_for_referral: '', additional_info: '', other_services_needed: '', referring_to: [] });
      setUploadedDocs([]);
      api.get('/her/participants').then(r => setParticipants(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  // ============ CLIENT DRILL-DOWN ============
  if (drilldownClient) {
    const cl = drilldownClient;
    const clCompletions = completionsByParticipant[cl.id] || [];
    const clEnrollments = enrollmentsByParticipant[cl.id] || [];
    const completedModules = clCompletions.filter(c => c.status === 'approved' || c.status === 'completed');
    const progressPct = modules.length ? Math.round((completedModules.length / modules.length) * 100) : 0;
    const clSessions = sessions.filter(s => (s.attendees || []).includes(cl.id));

    return (
      <div className="space-y-5" data-testid="her-client-drilldown">
        <Button variant="ghost" size="sm" onClick={() => setDrilldownClient(null)} className="rounded-full gap-1" data-testid="back-from-her-drilldown"><ArrowLeft size={16} /> Back</Button>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-pink-100 flex items-center justify-center text-xl font-bold text-pink-700">{cl.full_name?.charAt(0)}</div>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>{cl.full_name}</h2>
            <div className="flex gap-2 mt-0.5">
              <Badge className="bg-pink-50 text-pink-700 border-0 text-xs">Referred: {formatDate(cl.enrollment_date || cl.created_at)}</Badge>
              {cl.program_type && <Badge variant="outline" className="text-[10px] capitalize">{cl.program_type}</Badge>}
            </div>
          </div>
        </div>

        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp size={14} /> HER Progress</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-3">
              <Progress value={progressPct} className="flex-1 h-3" /><span className="text-sm font-bold text-pink-600">{progressPct}%</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-pink-50 rounded-xl p-3"><p className="text-xl font-bold text-pink-600">{completedModules.length}</p><p className="text-[10px] text-muted-foreground">Modules</p></div>
              <div className="bg-sky-50 rounded-xl p-3"><p className="text-xl font-bold text-sky-600">{clSessions.length}</p><p className="text-[10px] text-muted-foreground">Sessions</p></div>
              <div className="bg-emerald-50 rounded-xl p-3"><p className="text-xl font-bold text-emerald-600">{clEnrollments.length}</p><p className="text-[10px] text-muted-foreground">Enrollments</p></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Trophy size={14} /> Module Progress</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {modules.map(mod => {
              const done = clCompletions.find(c => c.module_id === mod.id && (c.status === 'approved' || c.status === 'completed'));
              const inProgress = clCompletions.find(c => c.module_id === mod.id && c.status === 'pending');
              return (
                <div key={mod.id} className={`flex items-center justify-between p-2.5 rounded-xl ${done ? 'bg-emerald-50 border border-emerald-200' : inProgress ? 'bg-amber-50 border border-amber-200' : 'bg-muted/20'}`}>
                  <div className="flex items-center gap-2">
                    {done ? <CheckCircle size={14} className="text-emerald-600" /> : inProgress ? <Clock size={14} className="text-amber-600" /> : <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300" />}
                    <span className="text-sm font-medium">{mod.name}</span>
                  </div>
                  <Badge className={done ? 'bg-emerald-100 text-emerald-700 border-0 text-[10px]' : inProgress ? 'bg-amber-100 text-amber-700 border-0 text-[10px]' : 'bg-slate-100 text-slate-500 border-0 text-[10px]'}>
                    {done ? `Completed ${formatDate(done.completed_at)}` : inProgress ? 'In Progress' : 'Not Started'}
                  </Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CalendarDays size={14} /> Attendance History</CardTitle></CardHeader>
          <CardContent>
            {clSessions.length > 0 ? (
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {clSessions.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/20 text-xs">
                    <span>{s.date} {s.time && `at ${s.time}`}</span>
                    <Badge variant="outline" className="text-[10px]">{s.type || 'Session'}</Badge>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No attendance records</p>}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============ MAIN PORTAL ============
  return (
    <div className="space-y-6" data-testid="her-referral-portal">
      <div><h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>H.E.R. Referral Portal</h1><p className="text-muted-foreground text-sm mt-1">Track referrals, monitor progress, and submit new referrals</p></div>

      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-pink-50 to-rose-50 border-l-4 border-l-pink-400" data-testid="her-submit-hero">
        <CardContent className="p-5 flex items-center justify-between">
          <div><h2 className="text-lg font-bold text-pink-900" style={{ fontFamily: 'Manrope' }}>Submit a New Referral</h2><p className="text-sm text-pink-700 mt-0.5">Refer someone to H.E.R., Purpose Driven, Our Square, or H.E.A.T.</p></div>
          <Button className="bg-pink-600 hover:bg-pink-700 text-white rounded-full gap-2 px-6" onClick={() => { setActiveTab('submit'); setSubmitted(false); }} data-testid="her-hero-submit-btn"><Send size={16} /> Submit Referral</Button>
        </CardContent>
      </Card>

      {/* Overview Stats — ALL CLICKABLE */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" onClick={() => setActiveTab('dashboard')} data-testid="her-stat-referrals">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 mx-auto mb-1 text-pink-500" />
            <p className="text-2xl font-bold text-pink-600">{participants.length}</p>
            <p className="text-xs text-muted-foreground">My Referred Clients</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" onClick={() => setActiveTab('active')} data-testid="her-stat-active">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-5 h-5 mx-auto mb-1 text-sky-500" />
            <p className="text-2xl font-bold text-sky-600">{activeEnrollments.length}</p>
            <p className="text-xs text-muted-foreground">Active in Program</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" onClick={() => setActiveTab('modules')} data-testid="her-stat-completed">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
            <p className="text-2xl font-bold text-emerald-600">{completedTotal}</p>
            <p className="text-xs text-muted-foreground">Modules Completed</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" onClick={() => setActiveTab('attendance')} data-testid="her-stat-sessions">
          <CardContent className="p-4 text-center">
            <CalendarDays className="w-5 h-5 mx-auto mb-1 text-violet-500" />
            <p className="text-2xl font-bold text-violet-600">{sessions.length}</p>
            <p className="text-xs text-muted-foreground">Sessions Held</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50 p-1 rounded-xl">
          <TabsTrigger value="dashboard" className="rounded-lg data-[state=active]:bg-white">My Referrals</TabsTrigger>
          <TabsTrigger value="active" className="rounded-lg data-[state=active]:bg-white">Active</TabsTrigger>
          <TabsTrigger value="modules" className="rounded-lg data-[state=active]:bg-white">Modules</TabsTrigger>
          <TabsTrigger value="attendance" className="rounded-lg data-[state=active]:bg-white">Attendance</TabsTrigger>
          <TabsTrigger value="facilitators" className="rounded-lg data-[state=active]:bg-white">Facilitators</TabsTrigger>
          <TabsTrigger value="submit" className="rounded-lg data-[state=active]:bg-pink-50 data-[state=active]:text-pink-700">Submit Referral</TabsTrigger>
        </TabsList>

        {/* MY REFERRALS TAB */}
        <TabsContent value="dashboard">
          <Card className="border-0 shadow-sm rounded-2xl" data-testid="her-referrals-list">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users size={16} /> Clients I've Referred ({participants.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {participants.map(p => {
                const pCompletions = completionsByParticipant[p.id] || [];
                const completed = pCompletions.filter(c => c.status === 'approved' || c.status === 'completed').length;
                const pct = modules.length ? Math.round((completed / modules.length) * 100) : 0;
                const pSessions = sessions.filter(s => (s.attendees || []).includes(p.id));
                return (
                  <div key={p.id} className="p-3 rounded-xl border hover:bg-pink-50/50 cursor-pointer transition-colors" onClick={() => setDrilldownClient(p)} data-testid={`her-referral-client-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-sm font-bold text-pink-700">{p.full_name?.charAt(0)}</div>
                        <div>
                          <p className="text-sm font-medium">{p.full_name}</p>
                          <div className="flex gap-1.5 mt-0.5">
                            <Badge className="bg-pink-50 text-pink-700 border-0 text-[10px]">{completed}/{modules.length} modules</Badge>
                            <Badge className="bg-sky-50 text-sky-700 border-0 text-[10px]">{pSessions.length} sessions</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="w-20"><Progress value={pct} className="h-2" /><p className="text-[10px] text-muted-foreground text-right mt-0.5">{pct}%</p></div>
                        <ChevronRight size={14} className="text-pink-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
              {participants.length === 0 && <div className="text-center py-6"><Users className="w-10 h-10 mx-auto mb-2 text-muted-foreground" /><p className="text-muted-foreground">No referred clients yet.</p></div>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ACTIVE IN PROGRAM TAB */}
        <TabsContent value="active">
          <Card className="border-0 shadow-sm rounded-2xl" data-testid="her-active-enrollments">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp size={16} /> Active Enrollments ({activeEnrollments.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {activeEnrollments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No active enrollments</p>
              ) : activeEnrollments.map(en => {
                const cid = en.client_id || en.participant_id;
                const p = participantMap[cid];
                const pCompletions = completionsByParticipant[cid] || [];
                const completedCount = pCompletions.filter(c => c.status === 'approved' || c.status === 'completed').length;
                const progressPct = modules.length ? Math.round((completedCount / modules.length) * 100) : 0;
                return (
                  <div key={en.id || cid} className="p-3 rounded-xl border hover:bg-sky-50/50 cursor-pointer transition-colors" onClick={() => p && setDrilldownClient(p)} data-testid={`her-active-${cid}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sm font-bold text-sky-700">{(p?.full_name || en.client_name || '?')[0]}</div>
                        <div>
                          <p className="text-sm font-medium">{p?.full_name || en.client_name || 'Unknown'}</p>
                          <div className="flex gap-1.5 mt-0.5 flex-wrap">
                            <Badge className="bg-sky-50 text-sky-700 border-0 text-[10px] capitalize">{en.status}</Badge>
                            <Badge className="bg-pink-50 text-pink-700 border-0 text-[10px]">{completedCount}/{modules.length} modules</Badge>
                            {(p?.drug_of_choice || en.drug_of_choice) && <Badge variant="outline" className="text-[10px]">DOC: {p?.drug_of_choice || en.drug_of_choice}</Badge>}
                            {(p?.phase || en.phase) && <Badge variant="outline" className="text-[10px]">{p?.phase || en.phase}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="w-20"><Progress value={progressPct} className="h-2" /><p className="text-[10px] text-muted-foreground text-right mt-0.5">{progressPct}%</p></div>
                        <ChevronRight size={14} className="text-sky-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MODULES TAB — with drill-down */}
        <TabsContent value="modules">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Trophy size={16} /> HER Modules & Completions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {modules.map(mod => {
                const modCompletions = completions.filter(c => c.module_id === mod.id);
                const approved = modCompletions.filter(c => c.status === 'approved' || c.status === 'completed');
                const pending = modCompletions.filter(c => c.status === 'pending');
                const isExpanded = expandedModule === mod.id;
                return (
                  <div key={mod.id} className="rounded-xl border overflow-hidden" data-testid={`her-module-drill-${mod.id}`}>
                    <div className="p-3 cursor-pointer hover:bg-pink-50/30 transition-colors flex items-center justify-between" onClick={() => setExpandedModule(isExpanded ? null : mod.id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-pink-50 flex items-center justify-center"><Trophy size={16} className="text-pink-600" /></div>
                        <div>
                          <p className="text-sm font-medium">{mod.name}</p>
                          {mod.description && <p className="text-[10px] text-muted-foreground">{mod.description}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-emerald-50 text-emerald-700 border-0 text-xs">{approved.length} completed</Badge>
                        {pending.length > 0 && <Badge className="bg-amber-50 text-amber-700 border-0 text-xs">{pending.length} pending</Badge>}
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t space-y-1.5 pt-2 bg-slate-50/50">
                        <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mb-1">Participants</p>
                        {approved.length === 0 && pending.length === 0 && <p className="text-xs text-muted-foreground">No completions yet.</p>}
                        {approved.map(comp => {
                          const p = participantMap[comp.participant_id];
                          return (
                            <div key={comp.id} className="flex items-center justify-between p-2 rounded-lg bg-emerald-50/60 border border-emerald-100 cursor-pointer hover:bg-emerald-50" onClick={() => p && setDrilldownClient(p)}>
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">{(p?.full_name || '?')[0]}</div>
                                <span className="text-xs font-medium">{p?.full_name || 'Unknown'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] text-muted-foreground">{formatDate(comp.completed_at)}</span>
                                <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Completed</Badge>
                                <ChevronRight size={12} className="text-emerald-400" />
                              </div>
                            </div>
                          );
                        })}
                        {pending.map(comp => {
                          const p = participantMap[comp.participant_id];
                          return (
                            <div key={comp.id} className="flex items-center justify-between p-2 rounded-lg bg-amber-50/60 border border-amber-100 cursor-pointer hover:bg-amber-50" onClick={() => p && setDrilldownClient(p)}>
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">{(p?.full_name || '?')[0]}</div>
                                <span className="text-xs font-medium">{p?.full_name || 'Unknown'}</span>
                              </div>
                              <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Pending</Badge>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {modules.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No modules</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ATTENDANCE TAB — with session drill-down */}
        <TabsContent value="attendance">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarDays size={16} /> Session Attendance ({sessions.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
              {sessions.length > 0 ? sessions.slice(0, 30).map(s => {
                const isExpanded = expandedSession === s.id;
                const attendeeList = (s.attendees || []).map(aid => participantMap[aid]).filter(Boolean);
                return (
                  <div key={s.id} className="rounded-xl border overflow-hidden" data-testid={`her-session-drill-${s.id}`}>
                    <div className="p-3 cursor-pointer hover:bg-violet-50/30 transition-colors flex items-center justify-between" onClick={() => setExpandedSession(isExpanded ? null : s.id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center"><CalendarDays size={16} className="text-violet-600" /></div>
                        <div>
                          <p className="text-sm font-medium">{s.date} {s.time && `at ${s.time}`}</p>
                          <div className="flex gap-1.5 mt-0.5">
                            {s.facilitator_name && <span className="text-[10px] text-muted-foreground">Facilitator: {s.facilitator_name}</span>}
                            {s.topic && <Badge variant="outline" className="text-[10px] py-0">{s.topic}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-violet-50 text-violet-700 border-0 text-xs">{(s.attendees || []).length} attendees</Badge>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t space-y-1.5 pt-2 bg-slate-50/50">
                        <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mb-1">Attendees</p>
                        {attendeeList.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No attendees recorded.</p>
                        ) : attendeeList.map(p => {
                          const pCompletions = completionsByParticipant[p.id] || [];
                          const done = pCompletions.filter(c => c.status === 'approved' || c.status === 'completed').length;
                          return (
                            <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-white border cursor-pointer hover:bg-violet-50/50 transition-colors" onClick={() => setDrilldownClient(p)}>
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-pink-100 flex items-center justify-center text-xs font-bold text-pink-700">{p.full_name?.charAt(0)}</div>
                                <span className="text-xs font-medium">{p.full_name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-pink-50 text-pink-700 border-0 text-[10px]">{done}/{modules.length} modules</Badge>
                                <ChevronRight size={12} className="text-pink-400" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }) : <p className="text-sm text-muted-foreground text-center py-4">No sessions recorded</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FACILITATORS TAB — with drill-down */}
        <TabsContent value="facilitators">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><User size={16} /> Facilitators ({facilitators.length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {facilitators.map(f => {
                const isExpanded = expandedFacilitator === f.id;
                const fSessions = sessions.filter(s => s.facilitator_id === f.id || s.facilitator_name === f.name);
                const totalAttendees = fSessions.reduce((a, s) => a + (s.attendees || []).length, 0);
                return (
                  <div key={f.id} className="rounded-xl border overflow-hidden" data-testid={`her-facilitator-drill-${f.id}`}>
                    <div className="p-3 cursor-pointer hover:bg-pink-50/30 transition-colors flex items-center justify-between" onClick={() => setExpandedFacilitator(isExpanded ? null : f.id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-pink-100 flex items-center justify-center text-sm font-bold text-pink-700">{f.name?.charAt(0)}</div>
                        <div>
                          <p className="text-sm font-medium">{f.name}</p>
                          <p className="text-xs text-muted-foreground">{f.organization || ''} {f.specialization ? `| ${f.specialization}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={f.approved ? 'bg-emerald-50 text-emerald-700 border-0 text-xs' : 'bg-amber-50 text-amber-700 border-0 text-xs'}>{f.approved ? 'Approved' : 'Pending'}</Badge>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t space-y-2 pt-2 bg-slate-50/50">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-white rounded-lg p-2 text-center border"><p className="text-lg font-bold text-violet-600">{fSessions.length}</p><p className="text-[10px] text-muted-foreground">Sessions Led</p></div>
                          <div className="bg-white rounded-lg p-2 text-center border"><p className="text-lg font-bold text-pink-600">{totalAttendees}</p><p className="text-[10px] text-muted-foreground">Total Attendees</p></div>
                        </div>
                        {fSessions.length > 0 && (
                          <div>
                            <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mb-1">Recent Sessions</p>
                            {fSessions.slice(0, 5).map(s => (
                              <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-white border text-xs mb-1 cursor-pointer hover:bg-violet-50/50" onClick={() => { setActiveTab('attendance'); setExpandedSession(s.id); }}>
                                <span>{s.date} {s.time && `at ${s.time}`}</span>
                                <Badge className="bg-violet-50 text-violet-700 border-0 text-[10px]">{(s.attendees || []).length} attended</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {facilitators.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No facilitators</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SUBMIT REFERRAL TAB */}
        <TabsContent value="submit">
          <Card className="border-0 shadow-sm rounded-2xl"><CardHeader><CardTitle className="text-base flex items-center gap-2"><Send size={16} /> Submit New Referral</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {submitted ? (
                <div className="text-center py-8"><CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500" /><h3 className="text-lg font-semibold text-emerald-700">Referral Submitted!</h3><Button className="mt-4 bg-pink-600 hover:bg-pink-700 text-white rounded-full" onClick={() => { setSubmitted(false); setActiveTab('dashboard'); }}>View My Referrals</Button></div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Full Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Client's full name" data-testid="her-referral-name" /></div>
                    <div className="space-y-1.5"><Label>Date of Birth</Label><Input type="date" value={form.dob} onChange={e => setForm({...form, dob: e.target.value})} /></div>
                    <div className="space-y-1.5"><Label>Ethnicity</Label><Input value={form.ethnicity} onChange={e => setForm({...form, ethnicity: e.target.value})} placeholder="Ethnicity" /></div>
                    <div className="space-y-1.5"><Label>Race</Label><Input value={form.race} onChange={e => setForm({...form, race: e.target.value})} placeholder="Race" /></div>
                  </div>
                  <div className="space-y-2">
                    <Label>Refer To * <span className="text-muted-foreground font-normal">(Select one or more services)</span></Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[{ value: 'her', label: 'H.E.R.', icon: Heart, hint: 'HER Program' }, { value: 'purpose_driven', label: 'Purpose Driven', icon: Building2, hint: 'Peer Support' }, { value: 'our_square', label: 'Our Square', icon: Home, hint: 'Housing' }, { value: 'heat', label: 'H.E.A.T.', icon: Flame, hint: 'HEAT Program' }].map(opt => {
                        const Icon = opt.icon; const sel = form.referring_to.includes(opt.value);
                        return (<button key={opt.value} type="button" onClick={() => toggleService(opt.value)} className={`p-3 rounded-xl border-2 transition-all text-center ${sel ? 'border-pink-500 bg-pink-50 shadow-sm' : 'border-slate-200 hover:border-pink-300'}`} data-testid={`her-referral-to-${opt.value}`}><Icon size={18} className={`mx-auto mb-1 ${sel ? 'text-pink-600' : 'text-slate-400'}`} /><p className={`text-xs font-medium ${sel ? 'text-pink-700' : 'text-slate-600'}`}>{opt.label}</p><p className="text-[10px] text-muted-foreground">{opt.hint}</p>{sel && <CheckCircle size={12} className="mx-auto mt-1 text-pink-600" />}</button>);
                      })}
                    </div>
                  </div>
                  <div className="space-y-1.5"><Label>Reason for Referral *</Label><DictationInput value={form.reason_for_referral} onChange={(val) => setForm({...form, reason_for_referral: val})} placeholder="Why is this person being referred? (speak or type)" rows={3} data-testid="her-referral-reason" /></div>
                  <div className="space-y-1.5"><Label>Additional Information</Label><DictationInput value={form.additional_info} onChange={(val) => setForm({...form, additional_info: val})} placeholder="Other services needed, special considerations (speak or type)" rows={3} data-testid="her-referral-additional" /></div>
                  <div className="space-y-2"><Label>Documents & Evaluations</Label>
                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:border-pink-300 transition-colors">
                      <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" onChange={handleFileUpload} className="hidden" id="her-doc-upload" />
                      <label htmlFor="her-doc-upload" className="cursor-pointer"><Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" /><p className="text-sm text-muted-foreground">{uploadingFiles ? 'Uploading...' : 'Click to attach documents'}</p></label>
                    </div>
                    {uploadedDocs.length > 0 && <div className="space-y-1">{uploadedDocs.map(d => (<div key={d.id} className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 text-xs"><Paperclip size={12} className="text-emerald-600" /><span className="font-medium text-emerald-700">{d.filename}</span></div>))}</div>}
                  </div>
                  <Button className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-full gap-2 py-3" onClick={handleSubmitReferral} disabled={submitting} data-testid="her-referral-submit"><Send size={16} /> {submitting ? 'Submitting...' : 'Submit Referral'}</Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
